/**
 * Shared highlight.js lazy-loader — used by both MarkdownContent and CodeViewer.
 * Loads core + curated language pack (~45KB) on first call, then caches the instance.
 */

type HljsInstance = {
  highlight: (code: string, opts: { language: string }) => { value: string };
  getLanguage: (name: string) => unknown;
  highlightElement: (el: HTMLElement) => void;
};

let hljsReady: Promise<HljsInstance> | null = null;

export function getHljs(): Promise<HljsInstance> {
  if (!hljsReady) {
    hljsReady = import('highlight.js/lib/core').then(async (core) => {
      const hljs = core.default;
      const langs = await Promise.all([
        import('highlight.js/lib/languages/typescript'),
        import('highlight.js/lib/languages/javascript'),
        import('highlight.js/lib/languages/bash'),
        import('highlight.js/lib/languages/sql'),
        import('highlight.js/lib/languages/python'),
        import('highlight.js/lib/languages/json'),
        import('highlight.js/lib/languages/yaml'),
        import('highlight.js/lib/languages/css'),
        import('highlight.js/lib/languages/xml'),
        import('highlight.js/lib/languages/dockerfile'),
        import('highlight.js/lib/languages/markdown'),
        import('highlight.js/lib/languages/go'),
        import('highlight.js/lib/languages/rust'),
      ]);
      const names = ['typescript', 'javascript', 'bash', 'sql', 'python', 'json', 'yaml', 'css', 'xml', 'dockerfile', 'markdown', 'go', 'rust'];
      langs.forEach((lang, idx) => hljs.registerLanguage(names[idx], lang.default));
      // Aliases
      hljs.registerLanguage('ts', (langs[0] as any).default);
      hljs.registerLanguage('js', (langs[1] as any).default);
      hljs.registerLanguage('sh', (langs[2] as any).default);
      hljs.registerLanguage('shell', (langs[2] as any).default);
      hljs.registerLanguage('zsh', (langs[2] as any).default);
      hljs.registerLanguage('html', (langs[8] as any).default);
      hljs.registerLanguage('yml', (langs[6] as any).default);
      return hljs as unknown as HljsInstance;
    });
  }
  return hljsReady;
}
